/**
 * 只为工作量证明写的 SHA-256：输入是 ≤ 55 字节的 ASCII 串（"{salt}:{nonce}"），正好一个分组。
 *
 * 不用 crypto.subtle：它每次都是异步的，几十万次调用光是 Promise 的开销就要好几秒；
 * 同步地在一个预分配的缓冲区里算，快一个数量级。
 */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
])
const INITIAL = [
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
]
const W = new Uint32Array(64)
const OUT = new Uint32Array(8)

/** 返回 8 个 32 位大端字（复用同一个数组：拿到就用，别存着） */
export function sha256Words(text: string): Uint32Array {
  const length = text.length
  if (length > 55) throw new Error('sha256Words 只处理 ≤ 55 字节的输入')
  W.fill(0, 0, 16)
  for (let i = 0; i < length; i++) {
    W[i >> 2] |= (text.charCodeAt(i) & 0xff) << (24 - (i & 3) * 8)
  }
  W[length >> 2] |= 0x80 << (24 - (length & 3) * 8)
  W[15] = length * 8
  for (let i = 16; i < 64; i++) {
    const w15 = W[i - 15]
    const w2 = W[i - 2]
    const s0 = ((w15 >>> 7) | (w15 << 25)) ^ ((w15 >>> 18) | (w15 << 14)) ^ (w15 >>> 3)
    const s1 = ((w2 >>> 17) | (w2 << 15)) ^ ((w2 >>> 19) | (w2 << 13)) ^ (w2 >>> 10)
    W[i] = (W[i - 16] + s0 + W[i - 7] + s1) | 0
  }
  let [a, b, c, d, e, f, g, h] = INITIAL
  for (let i = 0; i < 64; i++) {
    const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7))
    const ch = (e & f) ^ (~e & g)
    const t1 = (h + S1 + ch + K[i] + W[i]) | 0
    const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10))
    const maj = (a & b) ^ (a & c) ^ (b & c)
    const t2 = (S0 + maj) | 0
    h = g
    g = f
    f = e
    e = (d + t1) | 0
    d = c
    c = b
    b = a
    a = (t1 + t2) | 0
  }
  OUT[0] = INITIAL[0] + a
  OUT[1] = INITIAL[1] + b
  OUT[2] = INITIAL[2] + c
  OUT[3] = INITIAL[3] + d
  OUT[4] = INITIAL[4] + e
  OUT[5] = INITIAL[5] + f
  OUT[6] = INITIAL[6] + g
  OUT[7] = INITIAL[7] + h
  return OUT
}

export function leadingZeroBits(words: Uint32Array): number {
  let bits = 0
  for (const word of words) {
    if (word === 0) {
      bits += 32
      continue
    }
    return bits + Math.clz32(word)
  }
  return bits
}

/** 同步搜索 [start, start + count) 里第一个满足难度的 nonce；没有就返回 null */
export function search(salt: string, difficulty: number, start: number, count: number) {
  const prefix = `${salt}:`
  for (let nonce = start; nonce < start + count; nonce++) {
    if (leadingZeroBits(sha256Words(prefix + nonce)) >= difficulty) return nonce
  }
  return null
}
